import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Sparkles, Copy, Check } from "lucide-react";

interface EmailGeneratorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactId: string;
  jobListingId?: string;
  onEmailGenerated: (subject: string, body: string) => void;
}

export function EmailGeneratorDialog({
  open,
  onOpenChange,
  contactId,
  jobListingId,
  onEmailGenerated,
}: EmailGeneratorDialogProps) {
  const [purpose, setPurpose] = useState("introduction");
  const [tone, setTone] = useState("professional");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedEmail, setGeneratedEmail] = useState<{ subject: string; body: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const generateEmail = async () => {
    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('jh_generate_outreach_email', {
        body: { contactId, jobListingId, purpose, tone }
      });

      if (error) throw error;

      setGeneratedEmail({ subject: data.subject, body: data.body });
      toast.success('Email generated successfully');
    } catch (error: any) {
      console.error('Error generating email:', error);
      toast.error(error.message || 'Failed to generate email');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    if (!generatedEmail) return;
    await navigator.clipboard.writeText(`Subject: ${generatedEmail.subject}\n\n${generatedEmail.body}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard');
  };

  const useEmail = () => {
    if (!generatedEmail) return;
    onEmailGenerated(generatedEmail.subject, generatedEmail.body);
    onOpenChange(false);
    setGeneratedEmail(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Email Generator
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Email Purpose</Label>
            <Select value={purpose} onValueChange={setPurpose}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="introduction">Introduction</SelectItem>
                <SelectItem value="follow-up">Follow-up</SelectItem>
                <SelectItem value="application">Job Application</SelectItem>
                <SelectItem value="networking">Networking</SelectItem>
                <SelectItem value="referral">Referral Request</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Tone</Label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="professional">Professional</SelectItem>
                <SelectItem value="friendly">Friendly</SelectItem>
                <SelectItem value="formal">Formal</SelectItem>
                <SelectItem value="casual">Casual</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {!generatedEmail ? (
            <Button 
              onClick={generateEmail} 
              disabled={isGenerating}
              className="w-full"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate Email
                </>
              )}
            </Button>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>Subject</Label>
                <div className="p-3 bg-muted rounded-lg mt-2">
                  <p className="font-medium">{generatedEmail.subject}</p>
                </div>
              </div>

              <div>
                <Label>Body</Label>
                <div className="p-3 bg-muted rounded-lg mt-2 whitespace-pre-wrap">
                  {generatedEmail.body}
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={copyToClipboard} variant="outline" className="flex-1">
                  {copied ? (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="mr-2 h-4 w-4" />
                      Copy
                    </>
                  )}
                </Button>
                <Button onClick={useEmail} className="flex-1">
                  Use This Email
                </Button>
              </div>

              <Button 
                onClick={() => setGeneratedEmail(null)} 
                variant="ghost"
                className="w-full"
              >
                Generate New Email
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
